import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  getAllowedLabTypes, 
  getLabTypeName, 
  getInitialLabTypeId,
  getFirstLabTypeId, 
  shouldDisableDropdown,
  LAB_TYPES 
} from "../utils/labTypeFilter";

const EquipmentMaster = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  
  // Form state
  const [formData, setFormData] = useState({
    equipmentid: null,
    equipmentname: '',
    labtypeid: '',
    disciplineid: '',
    groupid: '',
    eqid: '',
    isactive: 1
  });

  // Dropdown states
  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filter state
  const [filterLabType, setFilterLabType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterGroup, setFilterGroup] = useState('');           // NEW
  const [filterDisciplines, setFilterDisciplines] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);         // NEW
  const [searchText, setSearchText] = useState('');

  // Pagination state - server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Table state
  const [equipmentData, setEquipmentData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Bulk upload states
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLabType, setBulkLabType] = useState('');
  const [bulkDiscipline, setBulkDiscipline] = useState('');
  const [bulkGroup, setBulkGroup] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);

  // Fetch disciplines when lab type changes
  useEffect(() => {
    if (formData.labtypeid) {
      fetchDisciplines(formData.labtypeid);
      setFormData(prev => ({ ...prev, disciplineid: '', groupid: '' }));
      setGroups([]);
    }
  }, [formData.labtypeid]);

  // Fetch groups when discipline changes
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid) {
      fetchGroups(formData.labtypeid, formData.disciplineid);
      setFormData(prev => ({ ...prev, groupid: '' }));
    }
  }, [formData.labtypeid, formData.disciplineid]);

  // ---------- NEW: Fetch filter groups when filter lab type & discipline change ----------
  useEffect(() => {
    if (filterLabType && filterDiscipline) {
      fetchFilterGroups(filterLabType, filterDiscipline);
    } else {
      setFilterGroups([]);
      setFilterGroup('');
    }
  }, [filterLabType, filterDiscipline]);

  // Get labTypeIds from localStorage and set initial labTypeId if only one is allowed
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultLabTypeId = null;
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        defaultLabTypeId = allowedIds[0];
        setFormData(prev => ({ ...prev, labtypeid: defaultLabTypeId }));
        setBulkLabType(defaultLabTypeId);
        setFilterLabType(defaultLabTypeId);
      }
    } else if (storedLabTypeId) {
      defaultLabTypeId = Number(storedLabTypeId);
      setFormData(prev => ({ ...prev, labtypeid: defaultLabTypeId }));
      setBulkLabType(defaultLabTypeId);
      setFilterLabType(defaultLabTypeId);
    }
  }, []);

  // Fetch equipment list when page, filters, or search changes - only if filterLabType is set
  useEffect(() => {
    if (filterLabType) {
      fetchEquipmentList();
    }
  }, [page, rowsPerPage, filterLabType, filterDiscipline, filterGroup, searchText]);

  // Handle filter disciplines when filter lab type changes
  useEffect(() => {
    if (filterLabType) {
      fetchFilterDisciplines(filterLabType);
      setFilterDiscipline('');
      setFilterGroup('');           // reset group when lab type changes
      setPage(0);
    } else {
      setFilterDisciplines([]);
      setFilterDiscipline('');
      setFilterGroups([]);
      setFilterGroup('');
    }
  }, [filterLabType]);

  // Reset page when discipline filter changes
  useEffect(() => {
    if (filterDiscipline) {
      setPage(0);
    }
  }, [filterDiscipline]);

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchText !== undefined) {
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(delayedSearch);
  }, [searchText]);

  // Fetch equipment list with pagination and filtering
  const fetchEquipmentList = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),          // NEW
        ...(searchText && { search: searchText.trim() })
      };

      const response = await axios.get(`${API_BASE_URL}/admin-master/equipment`, { params });

      if (response.data.status === 'success') {
        setEquipmentData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setError('');
      } else {
        setError('Failed to fetch equipment list');
        setEquipmentData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError(err.response?.data?.message || 'Error fetching equipment list');
      setEquipmentData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 100000,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),          // NEW
        ...(searchText && { search: searchText.trim() })
      };

      const response = await axios.get(`${API_BASE_URL}/admin-master/equipment`, { params });

      if (response.data.status !== "success") {
        setError("Failed to export data");
        return;
      }

      const allData = response.data.data || [];
      const exportData = allData.map((row, index) => {
        const labType = LAB_TYPES.find(lab => String(lab.id) === String(row.labtypeid));
        return {
          "S.No": index + 1,
          "CAB Type": labType?.name  || "N/A",
          "Equipment ID": row.equipmentid || "N/A",
          "Equipment Name": row.equipmentname || "N/A",
          "Discipline": row.DisciplineName || "N/A",
          "Group": row.groupName || "N/A",
          "Created On": row.createdon ? new Date(row.createdon).toLocaleDateString() : "N/A"
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EquipmentData");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(workbook, `Equipment_Master_${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      setError("Error exporting Excel");
    } finally {
      setLoading(false);
    }
  };

  // Fetch disciplines by lab type for form
  const fetchDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setDisciplines(response.data.data || []);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setDisciplines([]);
    }
  };

  // Fetch disciplines for filter dropdown
  const fetchFilterDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setFilterDisciplines(response.data.data || []);
      } else {
        setFilterDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching filter disciplines:', err);
      setFilterDisciplines([]);
    }
  };

  // ---------- NEW: Fetch groups for filter dropdown ----------
  const fetchFilterGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setFilterGroups(response.data.data || []);
      } else {
        setFilterGroups([]);
      }
    } catch (err) {
      console.error('Error fetching filter groups:', err);
      setFilterGroups([]);
    }
  };

  // Fetch groups by discipline for form/bulk
  const fetchGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setGroups(response.data.data || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission (Create/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.equipmentname || !formData.labtypeid || !formData.disciplineid) {
      setError('Equipment name, lab type, and discipline are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        equipmentname: formData.equipmentname,
        labtypeid: Number(formData.labtypeid),
        disciplineid: Number(formData.disciplineid),
        equipmentid: formData.eqid ? Number(formData.eqid) : null,
        GroupId: formData.groupid || null,
        isactive: formData.isactive
      };

      let response;
      let url = `${API_BASE_URL}/admin-master/equipment`;

      if (isEditMode && formData.equipmentid) {
        response = await axios.put(`${url}/${formData.equipmentid}`, payload);
      } else {
        response = await axios.post(url, payload);
      }

      if (response.data.status === 'success') {
        setSuccessMessage(isEditMode ? 'Equipment updated successfully!' : 'Equipment created successfully!');
        handleReset();
        fetchEquipmentList();
        setIsEditMode(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save equipment');
      }
    } catch (err) {
      console.error('Error saving equipment:', err);
      setError(err.response?.data?.message || 'Error saving equipment');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    setFormData({
      equipmentid: null,
      equipmentname: '',
      labtypeid: storedLabTypeId ? Number(storedLabTypeId) : '',
      disciplineid: '',
      groupid: '',
      eqid: '',
      isactive: 1
    });
    setIsEditMode(false);
    setDisciplines([]);
    setGroups([]);
  };

  // Handle edit
  const handleEdit = (equipment) => {
    setFormData({
      equipmentid: equipment.equipmentid,
      equipmentname: equipment.equipmentname,
      labtypeid: equipment.labtypeid,
      disciplineid: equipment.disciplineid,
      groupid: equipment.GroupId || '',
      eqid: equipment.eqid || '',
      isactive: equipment.isactive
    });
    setIsEditMode(true);
    
    if (equipment.labtypeid) {
      fetchDisciplines(equipment.labtypeid);
      fetchGroups(equipment.labtypeid, equipment.disciplineid);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle delete
  const handleDeleteClick = (eqid) => {
    setDeleteId(eqid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE_URL}/admin-master/equipment/${deleteId}`);
      if (response.data.status === 'success') {
        setSuccessMessage('Equipment deleted successfully!');
        fetchEquipmentList();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to delete equipment');
      }
    } catch (err) {
      console.error('Error deleting equipment:', err);
      setError(err.response?.data?.message || 'Error deleting equipment');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  // Bulk upload handlers
  const handleBulkUpload = () => {
    setBulkDialogOpen(true);
  };

  const handleBulkDialogClose = () => {
    setBulkDialogOpen(false);
    setBulkDiscipline('');
    setBulkGroup('');
    setBulkFile(null);
  };

  const handleBulkLabTypeChange = (e) => {
    const val = e.target.value;
    setBulkLabType(val);
    setBulkDiscipline('');
    setBulkGroup('');
    setBulkFile(null);
    if (val) {
      fetchDisciplines(val);
    }
  };

  const handleBulkDisciplineChange = (e) => {
    const val = e.target.value;
    setBulkDiscipline(val);
    setBulkGroup('');
    setBulkFile(null);
    if (bulkLabType && val) {
      fetchGroups(bulkLabType, val);
    }
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    setBulkFile(file || null);
  };

  const handleDownloadTemplate = () => {
    if (!bulkLabType) {
      setError('Please select Lab Type to download template');
      return;
    }
    const header = [['EquipmentName']];
    const ws = XLSX.utils.aoa_to_sheet(header);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    const lab = LAB_TYPES.find(l => l.id === bulkLabType)?.name || 'lab';
    XLSX.writeFile(wb, `EquipmentTemplate_${lab}.xlsx`);
  };

  const parseFile = async (file) => {
    const required = ['EquipmentName'];
    const ext = file.name.split('.').pop().toLowerCase();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (ext === 'xlsx' || ext === 'xls') {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!json.length) return reject(new Error('File is empty'));
            const headers = json[0].map(h => (h || '').toString().trim());
            const missing = required.filter(r => !headers.includes(r));
            if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
            const rows = json.slice(1).map(rowArr => {
              const obj = {};
              headers.forEach((h, idx) => { obj[h] = rowArr[idx] ? rowArr[idx].toString().trim() : ''; });
              return obj;
            }).filter(r => r.EquipmentName);
            resolve(rows);
          } catch (e) {
            reject(new Error('Invalid Excel file'));
          }
        } else {
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
          if (lines.length === 0) return reject(new Error('File is empty'));
          const headers = lines[0].split(',').map(h => h.trim());
          const missing = required.filter(r => !headers.includes(r));
          if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
          const rows = lines.slice(1).map(line => {
            const cols = line.split(',');
            const obj = {};
            headers.forEach((h, idx) => { obj[h] = cols[idx] ? cols[idx].trim() : ''; });
            return obj;
          }).filter(r => r.EquipmentName);
          resolve(rows);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      if (ext === 'xlsx' || ext === 'xls') {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const handleBulkUploadSubmit = async () => {
    setError('');
    setSuccessMessage('');
    if (!bulkLabType) {
      setError('Please select Lab Type');
      return;
    }
    if (!bulkDiscipline) {
      setError('Please select Discipline');
      return;
    }
    if (!bulkFile) {
      setError('Please choose an Excel/CSV file to upload');
      return;
    }

    try {
      setUploadLoading(true);
      const rows = await parseFile(bulkFile);
      if (!rows.length) {
        setError('No valid rows found in file');
        setUploadLoading(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;
      let failRows = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const payload = {
          equipmentname: row.EquipmentName,
          labtypeid: bulkLabType,
          disciplineid: bulkDiscipline,
          GroupId: bulkGroup || null,
          isactive: 1
        };
        try {
          const response = await axios.post(`${API_BASE_URL}/admin-master/equipment`, payload);
          if (response.data.status === 'success') {
            successCount++;
          } else {
            failCount++;
            failRows.push({ row: i + 2, error: response.data.message });
          }
        } catch (err) {
          failCount++;
          failRows.push({ row: i + 2, error: err.response?.data?.message || err.message });
        }
      }

      setSuccessMessage(`Bulk upload complete. Success: ${successCount}, Failed: ${failCount}`);
      if (failRows.length) {
        setError('Failed rows: ' + failRows.map(f => `Row ${f.row}: ${f.error}`).join('; '));
      }
      fetchEquipmentList();
      handleBulkDialogClose();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.response?.data?.message || err.message || 'Bulk upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: "#1976d2", mb: 4 }}>
        Equipment Master
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {/* Form Section (hidden as per original) */}
      <Card elevation={3} sx={{ mb: 4, display: "none" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            {isEditMode ? "Edit Equipment" : "Add New Equipment"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Equipment Name"
                  name="equipmentname"
                  value={formData.equipmentname}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  placeholder="e.g., µC NEPHLO TURBIDITY METER 135"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="labtypeid"
                  value={formData.labtypeid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={shouldDisableDropdown(filteredLabTypes)}
                >
                  <MenuItem value="" disabled><em>--Select Lab Type--</em></MenuItem>
                  {filteredLabTypes.map((lab) => (
                    <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="disciplineid"
                  value={formData.disciplineid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={!formData.labtypeid}
                >
                  <MenuItem value="" disabled><em>--Select Discipline--</em></MenuItem>
                  {disciplines.map((discipline) => (
                    <MenuItem key={discipline.id} value={discipline.id}>
                      {discipline.disciplineName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="groupid"
                  value={formData.groupid}
                  onChange={handleInputChange}
                  displayEmpty
                  variant="outlined"
                  disabled={!formData.disciplineid}
                >
                  <MenuItem value=""><em>--Select Group--</em></MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.grpId} value={group.grpId}>
                      {group.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading}
                sx={{ backgroundColor: "#1976d2" }}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>
              <Button variant="outlined" onClick={handleReset} startIcon={<RefreshIcon />} disabled={loading}>
                Reset
              </Button>
              <Button variant="outlined" onClick={handleBulkUpload} startIcon={<UploadIcon />} disabled={loading}>
                Bulk Upload
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Equipment Details Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Equipment Details Master
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterLabType}
              onChange={(e) => {
                setFilterLabType(e.target.value);
                setFilterDiscipline("");
                setFilterGroup("");
                if (e.target.value) {
                  fetchFilterDisciplines(e.target.value);
                } else {
                  setFilterDisciplines([]);
                  setFilterGroups([]);
                }
              }}
              renderValue={(selected) => {
                if (selected === "") return <em>--All Lab Types--</em>;
                const labType = LAB_TYPES.find((l) => l.id === selected);
                return labType?.name;
              }}
            >
              <MenuItem value=""><em>--All Lab Types--</em></MenuItem>
              {filteredLabTypes.map((lab) => (
                <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterDiscipline}
              onChange={(e) => {
                setFilterDiscipline(e.target.value);
                setFilterGroup("");   // reset group when discipline changes
              }}
              disabled={!filterLabType}
              renderValue={(selected) => {
                if (selected === "") return <em>--All Disciplines--</em>;
                const discipline = filterDisciplines.find(d => String(d.id) === String(selected));
                return discipline?.disciplineName || "N/A";
              }}
            >
              <MenuItem value=""><em>--All Disciplines--</em></MenuItem>
              {filterDisciplines.map((discipline) => (
                <MenuItem key={discipline.id} value={discipline.id}>
                  {discipline.disciplineName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* ---------- NEW: Group Filter ---------- */}
          {(filterLabType === 1 || filterLabType === 2) && (
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                displayEmpty
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                disabled={!filterLabType || !filterDiscipline}
                renderValue={(selected) => {
                  if (selected === "") return <em>--All Groups--</em>;
                  const group = filterGroups.find(g => g.grpId === selected);
                  return group?.groupName;
                }}
              >
                <MenuItem value=""><em>--All Groups--</em></MenuItem>
                {filterGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Equipment"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by equipment name, discipline..."
              size="medium"
            />
          </Grid>
          {/* Optional Export button */}
          {/* <Grid item xs={12} md={3}>
            <Button variant="contained" fullWidth startIcon={<DownloadIcon />} onClick={handleExportExcel} sx={{ height: "40px" }}>
              Export Excel
            </Button>
          </Grid> */}
        </Grid>

        {/* Results Counter */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {loading ? "Loading..." : `Showing ${equipmentData.length} of ${totalCount} equipments`}
          </Typography>
        </Box>

        {loading && !equipmentData.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>S.no</strong></TableCell>
                    <TableCell><strong>Equipment ID</strong></TableCell>
                    <TableCell><strong>Equipment Name</strong></TableCell>
                    <TableCell><strong>Lab Type</strong></TableCell>
                    <TableCell><strong>Discipline</strong></TableCell>
                    <TableCell><strong>Group</strong></TableCell>
                    <TableCell><strong>Created On</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {loading ? "Loading equipment data..." : "No equipment data available"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    equipmentData.map((row, index) => {
                      const labType = LAB_TYPES.find(lab => String(lab.id) === String(row.labtypeid));
                      return (
                        <TableRow key={row.equipmentid}>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{row.equipmentid}</TableCell>
                          <TableCell>{row.equipmentname}</TableCell>
                          <TableCell>{labType?.name || "N/A"}</TableCell>
                          <TableCell>{row.DisciplineName || "N/A"}</TableCell>
                          <TableCell>{row.groupName || "N/A"}</TableCell>
                          <TableCell>{row.createdon || "N/A"}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onClose={handleBulkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Equipment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkLabType}
                onChange={handleBulkLabTypeChange}
                disabled={shouldDisableDropdown(filteredLabTypes)}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Lab Type--</em>;
                  return LAB_TYPES.find(l => l.id === selected)?.name || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select Lab Type--</em></MenuItem>
                {filteredLabTypes.map((l) => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkDiscipline}
                onChange={handleBulkDisciplineChange}
                disabled={!bulkLabType}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Discipline--</em>;
                  const d = disciplines.find(dd => dd.id === selected);
                  return d?.disciplineName || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select Discipline--</em></MenuItem>
                {disciplines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.disciplineName}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkGroup}
                onChange={(e) => setBulkGroup(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Group (optional)--</em>;
                  const g = groups.find(gg => gg.grpId === selected);
                  return g?.groupName || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select Group (optional)--</em></MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.grpId} value={g.grpId}>{g.groupName}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button variant="outlined" onClick={handleDownloadTemplate} fullWidth>
                Download Template
              </Button>
            </Grid>
            <Grid item xs={12}>
              <input
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: "block" }}
                id="bulk-file-input"
                type="file"
                onChange={handleBulkFileChange}
              />
              {bulkFile && <Typography variant="body2" sx={{ mt: 1 }}>{bulkFile.name}</Typography>}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDialogClose} disabled={uploadLoading}>Cancel</Button>
          <Button
            onClick={handleBulkUploadSubmit}
            variant="contained"
            disabled={uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={18} /> : <UploadIcon />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this equipment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentMaster;